import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Alert, Skeleton,
  Grid, Paper, CircularProgress,
} from '@mui/material';
import { Kitchen, AutoAwesome, Shuffle } from '@mui/icons-material';
import { useRecipes } from '../../contexts/RecipeContext';
import { CATEGORIES } from '../../config';
import RecipeCard from '../recipes/RecipeCard';
import { API_BASE } from '../../config';

function matchIngredients(recipeIngredients, pantryItems) {
  const inStockItems = pantryItems.filter((p) => p.inStock);
  if (!recipeIngredients?.length) return { missing: [], present: [] };
  const missing = [];
  const present = [];
  for (const ing of recipeIngredients) {
    const ingName = ing.name?.toLowerCase() || '';
    const found = inStockItems.some(
      (p) => p.name.toLowerCase().includes(ingName) || ingName.includes(p.name.toLowerCase())
    );
    if (found) present.push(ing); else missing.push(ing);
  }
  return { missing, present };
}

function SectionHeader({ emoji, title, color, count }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Typography sx={{ fontSize: '1.5rem' }}>{emoji}</Typography>
      <Typography variant="h6">{title}</Typography>
      <Chip label={count} size="small"
        sx={{ bgcolor: `${color}20`, color, border: `1px solid ${color}40`, height: 22 }} />
    </Box>
  );
}

function AlmostCard({ recipe, missing, navigate }) {
  const category = CATEGORIES.find((c) => c.value === recipe.category);
  return (
    <Paper
      onClick={() => navigate(`/recipes/${recipe.id}`)}
      sx={{
        p: 2, cursor: 'pointer', borderRadius: 3,
        border: '1px solid rgba(255,165,38,0.15)',
        bgcolor: 'rgba(255,165,38,0.04)',
        transition: 'transform 0.15s',
        '&:hover': { transform: 'translateY(-2px)', borderColor: 'rgba(255,165,38,0.3)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, mr: 1 }}>{recipe.title}</Typography>
        {category && <Typography sx={{ fontSize: '1.2rem' }}>{category.emoji}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Missing:</Typography>
        {missing.map((ing, i) => (
          <Chip key={i} label={ing.name} size="small" sx={{
            height: 18, fontSize: '0.68rem',
            bgcolor: 'rgba(239,83,80,0.1)', color: 'error.main',
            border: '1px solid rgba(239,83,80,0.2)',
          }} />
        ))}
      </Box>
    </Paper>
  );
}

export default function WhatCanICookPage() {
  const navigate = useNavigate();
  const { recipes, pantry, pantryLoading, recipesLoading } = useRecipes();
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const hasPantryItems = pantry?.items?.some((i) => i.inStock);
  const inStockItems = pantry?.items?.filter((i) => i.inStock) || [];

  const categorized = useMemo(() => {
    if (!hasPantryItems) return { ready: [], almost: [], need: [] };
    const ready = [], almost = [], need = [];
    for (const recipe of recipes) {
      if (!recipe.ingredients?.length) { ready.push({ recipe, missing: [] }); continue; }
      const { missing } = matchIngredients(recipe.ingredients, pantry.items || []);
      if (missing.length === 0) ready.push({ recipe, missing: [] });
      else if (missing.length <= 3) almost.push({ recipe, missing });
      else need.push({ recipe, missing });
    }
    return { ready, almost, need };
  }, [recipes, pantry, hasPantryItems]);

  const handleRandomRecipe = () => {
    if (!categorized.ready.length) return;
    const pick = categorized.ready[Math.floor(Math.random() * categorized.ready.length)];
    navigate(`/recipes/${pick.recipe.id}`);
  };

  const handleAiSuggest = async () => {
    setAiLoading(true);
    setAiError('');
    setAiSuggestion(null);
    try {
      const res = await fetch(`${API_BASE}/api/rb/suggest-from-pantry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pantryItems: inStockItems.map((i) => i.name) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setAiSuggestion(data);
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  if (recipesLoading || pantryLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" height={50} width="60%" />
        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} sx={{ mt: 2 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>What Can I Cook?</Typography>
        <Typography color="text.secondary">Based on what's in your pantry right now.</Typography>
      </Box>

      {recipes.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You haven't added any recipes yet.{' '}
          <Button size="small" onClick={() => navigate('/recipes/new')} sx={{ color: 'primary.main' }}>
            Add a recipe
          </Button>
        </Alert>
      )}

      {!hasPantryItems ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '3rem', mb: 2 }}>🧺</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>Your pantry is empty</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add ingredients to your pantry to see which recipes you can make.
          </Typography>
          <Button variant="contained" startIcon={<Kitchen />} onClick={() => navigate('/pantry')}
            sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)' }}>
            Go to Pantry
          </Button>
        </Paper>
      ) : (
        <>
          {/* Pantry summary + actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{inStockItems.length}</strong> items in stock ·{' '}
              <Button size="small" onClick={() => navigate('/pantry')} sx={{ color: 'primary.main', p: 0, minWidth: 0 }}>
                Update pantry
              </Button>
            </Typography>
            {categorized.ready.length > 0 && (
              <Button size="small" startIcon={<Shuffle />} onClick={handleRandomRecipe}
                variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.15)', ml: 'auto' }}>
                Surprise me
              </Button>
            )}
            <Button size="small" startIcon={aiLoading ? <CircularProgress size={14} /> : <AutoAwesome />}
              onClick={handleAiSuggest} disabled={aiLoading} variant="outlined"
              sx={{ borderColor: 'rgba(240,135,60,0.4)', color: 'primary.main' }}>
              AI Suggestion
            </Button>
          </Box>

          {/* AI Suggestion result */}
          {aiError && <Alert severity="error" sx={{ mb: 3 }}>{aiError}</Alert>}
          {aiSuggestion && (
            <Paper sx={{ p: 3, mb: 3, border: '1px solid rgba(240,135,60,0.25)', bgcolor: 'rgba(240,135,60,0.04)', borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutoAwesome sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="subtitle1" fontWeight={700}>{aiSuggestion.title}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {aiSuggestion.description}
              </Typography>
              {aiSuggestion.ingredients?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {aiSuggestion.ingredients.map((ing, i) => (
                    <Chip key={i} label={ing} size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(255,255,255,0.06)' }} />
                  ))}
                </Box>
              )}
              {aiSuggestion.quickTip && (
                <Typography variant="caption" color="text.secondary">💡 {aiSuggestion.quickTip}</Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Button size="small" variant="contained" onClick={() => navigate('/recipes/new')}
                  sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)', mr: 1 }}>
                  Save this recipe
                </Button>
                <Button size="small" onClick={() => setAiSuggestion(null)} sx={{ color: 'text.secondary' }}>
                  Dismiss
                </Button>
              </Box>
            </Paper>
          )}

          {categorized.ready.length === 0 && categorized.almost.length === 0 && categorized.need.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '3rem', mb: 2 }}>🤔</Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>No matches found</Typography>
              <Typography color="text.secondary">Add more items to your pantry or more recipes.</Typography>
            </Paper>
          )}

          {/* Ready to Cook */}
          {categorized.ready.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionHeader emoji="✅" title="Ready to Cook" color="#66bb6a" count={categorized.ready.length} />
              <Grid container spacing={2}>
                {categorized.ready.map(({ recipe }) => (
                  <Grid item xs={12} sm={6} md={4} key={recipe.id}>
                    <RecipeCard recipe={recipe} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Almost There */}
          {categorized.almost.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionHeader emoji="🛒" title="Almost There" color="#ffa726" count={categorized.almost.length} />
              <Grid container spacing={1.5}>
                {categorized.almost.map(({ recipe, missing }) => (
                  <Grid item xs={12} sm={6} key={recipe.id}>
                    <AlmostCard recipe={recipe} missing={missing} navigate={navigate} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Need Ingredients */}
          {categorized.need.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionHeader emoji="💡" title="Need Ingredients" color="#a89880" count={categorized.need.length} />
              <Grid container spacing={1.5}>
                {categorized.need.map(({ recipe, missing }) => (
                  <Grid item xs={12} sm={6} key={recipe.id}>
                    <AlmostCard recipe={recipe} missing={missing} navigate={navigate} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
